import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ACADEMY_LOGO } from "../data/assets";
import { Lock, Play, ArrowRight, Mail, ChevronDown, Loader2, ShieldCheck, Flame, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = "https://images.unsplash.com/photo-1556764420-e37ef4cdfa5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const TIER_IMG = {
  easy: "https://images.unsplash.com/photo-1650897877793-b79e08b89df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  intermediate: "https://images.unsplash.com/photo-1652190364244-96ad73c21c4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  challenging: "https://images.unsplash.com/photo-1542490101-4be93d1756e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};
const TIERS = [
  { id: "easy", label: "Foundations", tag: "Easy", color: "#E0E0E0", blurb: "Master the touch. Every legend started here." },
  { id: "intermediate", label: "Sharpen", tag: "Intermediate", color: "#D4AF37", blurb: "Add deception and speed. Beat your defender." },
  { id: "challenging", label: "Elite", tag: "Challenging", color: "#C41E24", blurb: "Game-changer moves. This is Next O level." },
];

function DrillCard({ d, unlocked, tierColor, i }) {
  const [play, setPlay] = useState(false);
  const thumb = d.youtube_id ? `https://img.youtube.com/vi/${d.youtube_id}/hqdefault.jpg` : TIER_IMG[d.category];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
      className="group relative bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
      style={{ boxShadow: `0 10px 40px rgba(0,0,0,0.4)` }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${tierColor}66`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
      data-testid={`drill-card-${d.id}`}>
      <div className="relative aspect-video bg-black overflow-hidden">
        {play && d.youtube_id ? (
          <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${d.youtube_id}?autoplay=1`}
            title={d.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
        ) : (
          <>
            <img loading="lazy" decoding="async" src={thumb} alt="" className={`w-full h-full object-cover transition-all duration-500 ${unlocked ? "" : "blur-md scale-110"}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            {unlocked ? (
              d.youtube_id ? (
                <button data-testid={`drill-play-${d.id}`} onClick={() => setPlay(true)}
                  className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                    style={{ background: tierColor }}>
                    <Play size={24} className="text-black ml-1" fill="currentColor" />
                  </span>
                </button>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 gap-1">
                  <Flame size={22} style={{ color: tierColor }} />
                  <span className="text-[10px] font-bold uppercase tracking-widest">Clip dropping soon</span>
                </div>
              )
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <Lock size={26} className="text-white/80" />
              </div>
            )}
          </>
        )}
        <span className="absolute top-2.5 left-2.5 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
          style={{ background: `${tierColor}22`, color: tierColor, border: `1px solid ${tierColor}55` }}>{d.category}</span>
      </div>
      <div className="p-4">
        <h3 className={`font-headings text-lg uppercase tracking-wide leading-tight text-white ${unlocked ? "" : "blur-[6px] select-none"}`}>{d.title}</h3>
        <p className={`text-sm text-white/60 mt-1.5 leading-relaxed ${unlocked ? "" : "blur-[6px] select-none"}`}>{d.description}</p>
        <p className="text-xs italic text-white/70 mt-3 pl-3 py-0.5" style={{ borderLeft: `3px solid ${tierColor}` }}>
          {unlocked ? d.standard : "The Next O Standard — unlock to reveal"}
        </p>
      </div>
    </motion.div>
  );
}

export default function FreeDrills() {
  const navigate = useNavigate();
  const [drills, setDrills] = useState([]);
  const [unlocked, setUnlocked] = useState(localStorage.getItem("nexto_drills_unlocked") === "1");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/free-drills`).then(({ data }) => setDrills(data.drills)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim().toLowerCase(), source: "free-drills" });
      localStorage.setItem("nexto_drills_unlocked", "1");
      localStorage.setItem("nexto_drills_email", email.trim().toLowerCase());
      setUnlocked(true);
      toast.success("You're in! Every drill is unlocked. 🔓");
    } catch {
      toast.error("Couldn't sign you up. Please try again.");
    } finally { setBusy(false); }
  };

  const scrollToDrills = () => document.getElementById("drills")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-clip">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden">
        <img loading="lazy" decoding="async" src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/30" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 20%, rgba(196,30,36,0.28), transparent 55%)" }} />
        <div className="relative z-10 max-w-5xl mx-auto w-full px-6 sm:px-10 pb-20">
          <motion.img initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            src={ACADEMY_LOGO} alt="The Next O Technical Academy" className="w-40 h-auto object-contain mb-6" />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <div className="inline-flex items-center gap-2 bg-[#C41E24]/15 border border-[#C41E24]/40 text-[#C41E24] px-3 py-1 rounded-full mb-5">
              <Flame size={13} /><span className="text-[11px] font-bold uppercase tracking-[0.25em]">Free Drill Sampler</span>
            </div>
            <h1 className="font-cover text-6xl sm:text-8xl uppercase leading-[0.85] tracking-tight">
              Train Like<br /><span className="text-[#C41E24]">You Mean It.</span>
            </h1>
            <p className="font-subheadings text-lg sm:text-xl text-white/75 mt-6 max-w-xl">
              12 real drills straight from the Next O playbook — <b className="text-white">Foundations to Elite.</b> Drop your email, unlock every clip, and start putting in the lonely reps that separate you.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <button data-testid="hero-unlock-btn" onClick={scrollToDrills}
                className="inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-7 py-4 rounded-sm transition-colors"
                style={{ boxShadow: "0 12px 40px rgba(196,30,36,0.45)" }}>
                Unlock The Drills <ArrowRight size={16} />
              </button>
              <span className="flex items-center gap-2 text-white/50 text-sm"><ShieldCheck size={15} className="text-[#D4AF37]" /> Free · no spam · just game</span>
            </div>
          </motion.div>
        </div>
        <button onClick={scrollToDrills} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 hover:text-white z-10 animate-bounce"><ChevronDown size={26} /></button>
      </section>

      {/* DRILLS */}
      <div id="drills" className="max-w-6xl mx-auto px-5 sm:px-10 py-20">
        {TIERS.map((tier) => {
          const list = drills.filter((d) => d.category === tier.id);
          if (!list.length) return null;
          return (
            <section key={tier.id} className="mb-20" data-testid={`tier-${tier.id}`}>
              <div className="flex items-end gap-3 mb-2">
                <h2 className="font-cover text-4xl sm:text-5xl uppercase tracking-tight" style={{ color: tier.color }}>{tier.label}</h2>
                <span className="text-[11px] font-bold uppercase tracking-widest text-white/40 mb-2">{tier.tag}</span>
              </div>
              <div className="h-1 w-20 rounded-full mb-3" style={{ background: tier.color }} />
              <p className="text-white/55 text-sm mb-7 max-w-lg">{tier.blurb}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {list.map((d, i) => <DrillCard key={d.id} d={d} i={i} unlocked={unlocked} tierColor={tier.color} />)}
              </div>
            </section>
          );
        })}

        {/* BOTTOM CTA */}
        <motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden border border-[#D4AF37]/25 p-10 sm:p-16 text-center mt-6"
          style={{ background: "radial-gradient(circle at 50% 0%, rgba(196,30,36,0.2), #0c0c0c 60%)" }} data-testid="drills-buy-cta">
          <div className="inline-flex items-center gap-2 text-[#D4AF37] mb-4"><Flame size={16} /><span className="text-[11px] font-bold uppercase tracking-[0.3em]">The Full System</span></div>
          <h2 className="font-cover text-4xl sm:text-6xl uppercase tracking-tight leading-none">Ready For The<br /><span className="text-[#C41E24]">Whole Playbook?</span></h2>
          <p className="text-white/60 max-w-xl mx-auto mt-5">This is a taste. Volume 1: Ball Mastery Blueprint is the complete elite technical system — full progressions, the Player Hub, streaks, coach feedback and the Next O Standard for every move.</p>
          <button data-testid="get-volume-btn" onClick={() => navigate("/store")}
            className="inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-8 py-4 rounded-sm mt-8 transition-colors"
            style={{ boxShadow: "0 12px 40px rgba(196,30,36,0.5)" }}>
            Enroll in the Blueprint <ArrowRight size={18} />
          </button>
        </motion.section>
      </div>

      {/* EMAIL GATE */}
      {!unlocked && (
        <motion.div initial={{ y: 120 }} animate={{ y: 0 }} transition={{ type: "spring", stiffness: 120, damping: 18 }}
          className="fixed bottom-0 inset-x-0 z-50 bg-black/85 backdrop-blur-xl border-t border-[#C41E24]/40 px-5 py-4"
          data-testid="email-gate">
          <form onSubmit={submit} className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-3">
            <div className="flex-1 text-center sm:text-left">
              <div className="font-headings text-lg uppercase tracking-wide flex items-center gap-2 justify-center sm:justify-start"><Lock size={16} className="text-[#C41E24]" /> Unlock All 12 Drills</div>
              <div className="text-xs text-white/50">Join the Next O newsletter — instant access, free forever.</div>
            </div>
            <div className="relative w-full sm:w-72">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
              <input data-testid="gate-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-white/5 border border-white/15 rounded-lg pl-10 pr-3 py-3 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
            </div>
            <button data-testid="gate-submit-btn" type="submit" disabled={busy}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-sm font-bold uppercase tracking-wider px-6 py-3 rounded-lg transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Unlock
            </button>
          </form>
        </motion.div>
      )}
    </div>
  );
}
